import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { Check, ArrowLeft, Info } from 'lucide-react';
import { useForgotPassword } from '../api';
import { useToast } from '@/hooks/useToast';
import { AuthShell, FloatField } from '../AuthShared';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

export const ForgotPasswordPage: React.FC = () => {
  const toast = useToast();
  const forgotMutation = useForgotPassword();
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      try {
        const { message } = await forgotMutation.mutateAsync({ email: value.email });
        setSentEmail(value.email);
        setSent(true);
        toast.success(message || 'If an account exists, a reset email was sent.');
      } catch (error: any) {
        toast.error(error?.message || 'Failed to request password reset.');
      }
    },
  });

  return (
    <AuthShell>
      {!sent ? (
        <>
          <div className="head">
            <h1>Reset your password</h1>
            <p>Enter your email and we&apos;ll send a secure reset link.</p>
          </div>

          {forgotMutation.error && (
            <div className="auth-error-banner" role="alert">
              {(forgotMutation.error as any)?.response?.data?.error?.message ||
                'Request failed. Please try again.'}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            noValidate
          >
            <form.Field
              name="email"
              validators={{
                onBlur: ({ value }) => {
                  const r = forgotPasswordSchema.shape.email.safeParse(value);
                  return r.success ? undefined : r.error.issues[0]?.message;
                },
                onSubmit: ({ value }) => {
                  const r = forgotPasswordSchema.shape.email.safeParse(value);
                  return r.success ? undefined : r.error.issues[0]?.message;
                },
              }}
            >
              {(field) => (
                <FloatField
                  id="forgot-email"
                  label="Personal email"
                  type="email"
                  icon="mail"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  autoComplete="email"
                  error={field.state.meta.errors[0] as string | undefined}
                />
              )}
            </form.Field>

            <button
              type="submit"
              disabled={forgotMutation.isPending}
              className="btn-auth-primary auth-cta"
              style={{ marginTop: 20 }}
            >
              {forgotMutation.isPending ? 'Sending…' : 'Send reset link'}
            </button>
          </form>

          <div className="auth-foot">
            Remembered it? <Link to="/login">Back to sign in</Link>
          </div>
        </>
      ) : (
        <div className="reset-success">
          <div className="reset-success-ic">
            <Check size={28} strokeWidth={2.5} />
          </div>
          <h1>Check your email</h1>
          <p>
            We sent a password reset link to{' '}
            <strong style={{ color: 'var(--ink)' }}>{sentEmail}</strong>. The link expires in 30
            minutes.
          </p>

          <div
            style={{
              marginTop: 20,
              padding: 14,
              background: 'var(--bg-2)',
              borderRadius: 10,
              fontSize: 12.5,
              color: 'var(--ink-3)',
              textAlign: 'left',
              display: 'flex',
              gap: 10,
            }}
          >
            <Info size={14} style={{ color: 'var(--ink-4)', flexShrink: 0, marginTop: 1 }} />
            <span>
              Didn&apos;t get the email? Check your spam folder, or{' '}
              <button
                type="button"
                onClick={() => setSent(false)}
                style={{
                  color: 'var(--ink-2)',
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                try a different address
              </button>
              .
            </span>
          </div>

          <Link to="/login" className="btn-auth-secondary" style={{ marginTop: 18 }}>
            <ArrowLeft size={14} />
            Back to sign in
          </Link>
        </div>
      )}
    </AuthShell>
  );
};
